import React, { useEffect, useMemo, useRef, useState } from 'react';
import { filterOptions } from './promptModel';

const POPOVER_HEIGHT = 340;

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M2.5 7.5l3 3 6-7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronsIcon = () => (
  <svg className="pe-dd-chevrons" width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true">
    <path d="M2 6l3-3 3 3M2 10l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * A dropdown that opens a list of choices, with a search box when `searchPlaceholder` is given.
 * Options are `{ value, label, description?, group? }`. A saved value missing from the list still
 * shows. `allowCustom` lets the search box offer what was typed as a value of its own.
 */
const Dropdown = ({ label, value, options, onChange, placeholder, searchPlaceholder, allowCustom = false, align = 'left', variant = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [opensUp, setOpensUp] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const root = useRef(null);
  const button = useRef(null);
  const list = useRef(null);
  const search = useRef(null);
  const isSearchable = Boolean(searchPlaceholder);

  const rows = useMemo(() => {
    const known = value === '' || options.some((option) => option.value === value);
    const all = known || query ? options : [{ value, label: value }, ...options];
    const shown = filterOptions(all, query);
    const typed = query.trim();
    if (allowCustom && typed && !options.some((option) => option.value === typed)) {
      return [...shown, { value: typed, label: `Use “${typed}”` }];
    }
    return shown;
  }, [allowCustom, options, query, value]);

  const shownValue = options.find((option) => option.value === value)?.label ?? (value === '' ? placeholder : value);

  const close = (returnFocus = true) => {
    setIsOpen(false);
    setQuery('');
    if (returnFocus) {
      button.current?.focus();
    }
  };

  const open = () => {
    const box = root.current.getBoundingClientRect();
    setOpensUp(box.bottom + POPOVER_HEIGHT > window.innerHeight && box.top > POPOVER_HEIGHT);
    setActiveIndex(Math.max(rows.findIndex((row) => row.value === value), 0));
    setIsOpen(true);
  };

  const choose = (row) => {
    if (row.value !== value) {
      onChange(row.value);
    }
    close();
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    (isSearchable ? search.current : list.current)?.focus();
    const onOutside = (event) => {
      if (!root.current?.contains(event.target)) {
        close(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [isOpen, isSearchable]);

  useEffect(() => {
    if (isOpen) {
      list.current?.querySelector('.is-active')?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, isOpen]);

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, rows.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && rows[activeIndex]) {
      event.preventDefault();
      choose(rows[activeIndex]);
    } else if (event.key === 'Tab') {
      close(false);
    }
  };

  let lastGroup = null;

  return (
    <div ref={root} className={`pe-dd ${variant}`.trim()}>
      <button
        ref={button}
        type="button"
        className="pe-dd-button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${label}: ${shownValue}`}
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={(event) => {
          if (!isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault();
            open();
          }
        }}
      >
        <span className={value === '' && placeholder ? 'pe-dd-value is-placeholder' : 'pe-dd-value'}>{shownValue}</span>
        <ChevronsIcon />
      </button>

      {isOpen && (
        // The list reacts to keys passed up from the search box and from itself.
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className={`pe-dd-popover${opensUp ? ' opens-up' : ''}${align === 'right' ? ' aligns-right' : ''}`} onKeyDown={onKeyDown}>
          {isSearchable && (
            <input
              ref={search}
              className="pe-dd-search"
              aria-label={searchPlaceholder}
              placeholder={searchPlaceholder}
              spellCheck={false}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
            />
          )}
          <div ref={list} className="pe-dd-list" role="listbox" aria-label={label} tabIndex={-1}>
            {rows.length === 0 && <p className="pe-dd-empty">Nothing found</p>}
            {rows.map((row, index) => {
              const heading = row.group && row.group !== lastGroup ? row.group : null;
              lastGroup = row.group || lastGroup;
              return (
                <React.Fragment key={row.value}>
                  {heading && <p className="pe-dd-group">{heading}</p>}
                  <button
                    type="button"
                    role="option"
                    aria-selected={row.value === value}
                    tabIndex={-1}
                    className={`pe-dd-option${index === activeIndex ? ' is-active' : ''}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => choose(row)}
                  >
                    <span className="pe-dd-option-text">
                      <span>{row.label}</span>
                      {row.description && <small>{row.description}</small>}
                    </span>
                    {row.value === value && <CheckIcon />}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
